// Settings — operator-mode toggles + engagement sender controls + account info.
// Toggles are optimistic — flip the cached value immediately, roll back on PATCH error.

import { useMemo, useState, type FormEvent } from 'react';
import { changePassword, signOut } from '../lib/auth';
import {
  useConfig,
  useMe,
  useRunEngagementSweep,
  useSetConfig,
  type AppConfigEntry,
} from '../lib/queries';

export function Settings() {
  const me = useMe();
  const cfgQ = useConfig();
  const setCfg = useSetConfig();

  const sweep = useRunEngagementSweep();

  // Track local optimistic toggle values
  const [pendingAutoSend, setPendingAutoSend] = useState<boolean | null>(null);
  const [pendingEngagementOn, setPendingEngagementOn] = useState<boolean | null>(null);
  const [pendingDryRun, setPendingDryRun] = useState<boolean | null>(null);

  const cfgByKey = useMemo(() => {
    const m = new Map<string, AppConfigEntry>();
    for (const r of cfgQ.data ?? []) m.set(r.key, r);
    return m;
  }, [cfgQ.data]);

  const autoSendServer = parseBool(cfgByKey.get('auto_send_enabled')?.value);
  const autoSend = pendingAutoSend ?? autoSendServer;
  const engagementOnServer = parseBool(cfgByKey.get('engagement_send_enabled')?.value);
  const engagementOn = pendingEngagementOn ?? engagementOnServer;
  const dryRunServer = parseBool(cfgByKey.get('engagement_dry_run')?.value);
  const dryRun = pendingDryRun ?? dryRunServer;
  const maxPerRun = (cfgByKey.get('engagement_max_per_run')?.value as number | undefined) ?? 50;
  const confidenceThreshold = cfgByKey.get('confidence_threshold')?.value as number | undefined;
  const maxInbound = cfgByKey.get('max_inbound_per_thread_auto')?.value as number | undefined;

  function makeToggle(
    key: string,
    current: boolean,
    setPending: (v: boolean | null) => void,
  ): () => void {
    return () => {
      const next = !current;
      setPending(next);
      setCfg.mutate(
        { key, value: next },
        {
          onSuccess: () => setPending(null),
          onError: () => setPending(null),
        },
      );
    };
  }

  const handleAutoSendToggle = makeToggle('auto_send_enabled', autoSend, setPendingAutoSend);
  const handleEngagementToggle = makeToggle('engagement_send_enabled', engagementOn, setPendingEngagementOn);
  const handleDryRunToggle = makeToggle('engagement_dry_run', dryRun, setPendingDryRun);

  return (
    <div className="page">
      <header className="page__head">
        <div className="page__rule">
          <span>Section 04</span>
          <span>Settings</span>
          <span>{me.data?.member.role ?? '—'}</span>
        </div>
        <h1 className="page__title">Settings</h1>
        <p className="page__deck">
          The operator switch decides whether approved drafts go out automatically.
          Everything else is currently read-only — change them in SQL.
        </p>
      </header>

      <section className="settings-section">
        <header className="settings-section__head">
          <h2>Operator mode</h2>
          <p className="settings-section__deck">
            With auto-send <strong>on</strong>, the system will dispatch approved
            replies on the next cron tick. With it <strong>off</strong>, drafts wait
            for a human approval (copilot mode).
          </p>
        </header>
        <div className="settings-toggle-row">
          <button
            type="button"
            role="switch"
            aria-checked={autoSend}
            disabled={setCfg.isPending}
            className={`settings-toggle${autoSend ? ' settings-toggle--on' : ''}`}
            onClick={handleAutoSendToggle}
          >
            <span className="settings-toggle__track" aria-hidden="true">
              <span className="settings-toggle__handle" />
            </span>
            <span className="settings-toggle__label">
              Auto-send replies · <strong>{autoSend ? 'ON' : 'OFF'}</strong>
            </span>
          </button>
          {setCfg.isError && (
            <p className="page__deck--error">
              Couldn’t update: <code>{setCfg.error?.message}</code>
            </p>
          )}
        </div>
      </section>

      {/* Engagement sender — separate from the reply auto-send above */}
      <section className="settings-section">
        <header className="settings-section__head">
          <h2>Engagement sender</h2>
          <p className="settings-section__deck">
            Drop-off / re-engagement emails fire daily at 9:00 AM ET to the seven cohorts.
            <strong> Master switch defaults OFF.</strong> Dry-run logs every send as
            <code> suppressed_dry_run</code> without calling Resend — use to validate
            eligibility before going live.
          </p>
        </header>

        <div className="settings-toggle-row">
          <button
            type="button"
            role="switch"
            aria-checked={engagementOn}
            disabled={setCfg.isPending}
            className={`settings-toggle${engagementOn ? ' settings-toggle--on' : ''}`}
            onClick={handleEngagementToggle}
          >
            <span className="settings-toggle__track" aria-hidden="true">
              <span className="settings-toggle__handle" />
            </span>
            <span className="settings-toggle__label">
              Engagement sweep · <strong>{engagementOn ? 'ON' : 'OFF'}</strong>
            </span>
          </button>
        </div>

        <div className="settings-toggle-row">
          <button
            type="button"
            role="switch"
            aria-checked={dryRun}
            disabled={setCfg.isPending || !engagementOn}
            className={`settings-toggle settings-toggle--secondary${dryRun ? ' settings-toggle--on' : ''}`}
            onClick={handleDryRunToggle}
          >
            <span className="settings-toggle__track" aria-hidden="true">
              <span className="settings-toggle__handle" />
            </span>
            <span className="settings-toggle__label">
              Dry-run mode · <strong>{dryRun ? 'ON' : 'OFF'}</strong>{' '}
              <span className="settings-toggle__note">
                {dryRun ? '(no Resend calls — log only)' : '(REAL sends)'}
              </span>
            </span>
          </button>
        </div>

        <dl className="settings-thresholds">
          <dt>Max per stage per run</dt>
          <dd>
            <code>{maxPerRun}</code>{' '}
            <span className="muted">throttle — at most this many sends per stage per cron tick</span>
          </dd>
        </dl>

        <div className="sweep-action">
          <button
            type="button"
            className="sweep-action__btn"
            onClick={() => sweep.mutate()}
            disabled={sweep.isPending}
          >
            {sweep.isPending ? 'Running sweep…' : 'Run engagement sweep now'}
          </button>
          <span className="sweep-action__hint">
            {engagementOn
              ? dryRun
                ? 'Will run eligibility queries and log dry rows.'
                : 'Will send real emails. Throttled to ' + maxPerRun + ' per stage.'
              : 'Master switch is OFF. Enable above first.'}
          </span>
        </div>

        {sweep.data && (
          <div className="sweep-result">
            {sweep.data.skipped ? (
              <p className="sweep-result__skipped">Skipped: {sweep.data.reason}</p>
            ) : (
              <ul className="sweep-result__list">
                {Object.entries(sweep.data.stages).map(([id, r]) => (
                  <li key={id}>
                    <strong>{id}</strong>: {r.eligible} eligible · {r.sent} sent ·{' '}
                    {r.dry} dry · {r.failed} failed
                    {r.errors.length > 0 && (
                      <span className="sweep-result__errs"> · errs: {r.errors.slice(0, 2).join('; ')}{r.errors.length > 2 && ` (+${r.errors.length - 2} more)`}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {sweep.isError && (
          <p className="page__deck--error">
            Sweep failed: <code>{sweep.error?.message}</code>
          </p>
        )}
      </section>

      <section className="settings-section">
        <header className="settings-section__head">
          <h2>Thresholds</h2>
          <p className="settings-section__deck">
            Used by the escalation logic. Read-only here — changing these requires
            a SQL update + a Worker re-deploy.
          </p>
        </header>
        <dl className="settings-thresholds">
          <dt>Confidence threshold</dt>
          <dd>
            <code>{confidenceThreshold ?? '—'}</code>{' '}
            <span className="muted">below this triggers low_confidence escalation</span>
          </dd>
          <dt>Max inbound per thread (auto)</dt>
          <dd>
            <code>{maxInbound ?? '—'}</code>{' '}
            <span className="muted">threads with more inbound messages get human-routed</span>
          </dd>
        </dl>
      </section>

      <section className="settings-section">
        <header className="settings-section__head">
          <h2>Account</h2>
        </header>
        {me.data ? (
          <dl className="settings-account">
            <dt>Display name</dt>
            <dd>{me.data.member.display_name}</dd>
            <dt>Email</dt>
            <dd><code>{me.data.member.email}</code></dd>
            <dt>Role</dt>
            <dd><code>{me.data.member.role}</code></dd>
          </dl>
        ) : (
          <p className="muted">Loading account…</p>
        )}
      </section>

      <section className="settings-section">
        <header className="settings-section__head">
          <h2>Change password</h2>
          <p className="settings-section__deck">
            Updates your Reply Console password (stored hashed in <code>ass.team_members</code>).
            Independent of the Stax product login.
          </p>
        </header>
        <ChangePasswordForm />
      </section>

      <footer className="settings-foot">
        <button type="button" className="signout" onClick={() => signOut()}>
          Sign out
        </button>
      </footer>
    </div>
  );
}

function parseBool(v: unknown): boolean {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'string') return v.toLowerCase() === 'true';
  return false;
}

function ChangePasswordForm() {
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setStatus(null);
    if (pw1.length < 8) {
      setStatus({ kind: 'err', msg: 'Password must be at least 8 characters.' });
      return;
    }
    if (pw1 !== pw2) {
      setStatus({ kind: 'err', msg: 'Passwords do not match.' });
      return;
    }
    setSubmitting(true);
    const result = await changePassword(pw1);
    setSubmitting(false);
    if (!result.ok) {
      setStatus({ kind: 'err', msg: result.error });
      return;
    }
    setStatus({ kind: 'ok', msg: 'Password updated. New password is active immediately.' });
    setPw1('');
    setPw2('');
  }

  return (
    <form className="pwchange" onSubmit={handleSubmit}>
      <label className="pwchange__field">
        <span>New password</span>
        <input
          type="password"
          autoComplete="new-password"
          required
          minLength={8}
          value={pw1}
          onChange={(e) => setPw1(e.target.value)}
        />
      </label>
      <label className="pwchange__field">
        <span>Confirm new password</span>
        <input
          type="password"
          autoComplete="new-password"
          required
          minLength={8}
          value={pw2}
          onChange={(e) => setPw2(e.target.value)}
        />
      </label>
      {status && (
        <div className={`pwchange__status pwchange__status--${status.kind}`}>{status.msg}</div>
      )}
      <button type="submit" className="pwchange__submit" disabled={submitting}>
        {submitting ? 'updating…' : 'update password'}
      </button>
    </form>
  );
}
